import * as React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { request } from "../../../../services/cpln";
import { useAppSelector } from "../../../../store/hooks";
import { selectLabel, selectValue } from "../../../../store/timezoneSlice";
import { BaseChart } from "../../BaseChart";
import {
  QueryRangeResponse,
  ChartDataPoint,
  getTimeRange,
  formatBytesPerSecond,
  getMetricsErrorMessage,
  buildByClause,
  translateGrouping,
  getAdaptiveTimeFormat,
} from "../../utils";

dayjs.extend(utc);
dayjs.extend(timezone);

const EMPTY_ARRAY: string[] = [];

export interface AgentNetworkChartProps {
  org: string;
  grouping?: string[];
  hideFromLabel?: string[];
  fromIso?: string | null;
  toIso?: string | null;
  step?: string;
}

export const AgentNetworkChart: React.FC<AgentNetworkChartProps> = ({
  org,
  grouping = EMPTY_ARRAY,
  hideFromLabel = EMPTY_ARRAY,
  fromIso,
  toIso,
  step = "30s",
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [chartData, setChartData] = React.useState<ChartDataPoint[]>([]);
  const [seriesKeys, setSeriesKeys] = React.useState<string[]>([]);

  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);

  React.useEffect(() => {
    if (org) {
      fetchMetrics();
    }
  }, [org, grouping, fromIso, toIso, step, timezoneValue]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const translatedGrouping = translateGrouping(grouping, "agent_name");
      const byClause = buildByClause(translatedGrouping);
      const rxQuery = `sum(rate(agent_rx_bytes_total{}[5m])) ${byClause}`;
      const txQuery = `sum(rate(agent_tx_bytes_total{}[5m])) ${byClause}`;
      const hasItemGrouping = grouping.includes("item");

      const { start, end } = getTimeRange(fromIso, toIso);

      const [rxResponse, txResponse] = await Promise.all([
        request<QueryRangeResponse>({
          service: "metrics",
          method: "post",
          url: `/metrics/org/${org}/api/v1/query_range?query=${encodeURIComponent(rxQuery)}&start=${start.toISOString()}&end=${end.toISOString()}&step=${step}`,
        }),
        request<QueryRangeResponse>({
          service: "metrics",
          method: "post",
          url: `/metrics/org/${org}/api/v1/query_range?query=${encodeURIComponent(txQuery)}&start=${start.toISOString()}&end=${end.toISOString()}&step=${step}`,
        }),
      ]);

      const dataMap = new Map<number, ChartDataPoint>();
      const keysSet = new Set<string>();

      // Build series key based on grouping
      const buildSeriesKey = (metric: Record<string, string>, suffix: string) => {
        if (!hasItemGrouping) return suffix;
        const groupingPart = translatedGrouping
          .filter((g) => !hideFromLabel.includes(g))
          .map((g) => metric[g] || "unknown")
          .filter(Boolean)
          .join("/");
        return groupingPart ? `${groupingPart} (${suffix})` : suffix;
      };

      // Process RX data
      if (rxResponse.data.status === "success" && rxResponse.data.data?.result) {
        for (const series of rxResponse.data.data.result) {
          const key = buildSeriesKey(series.metric, "RX");
          keysSet.add(key);

          for (const [timestamp, value] of series.values) {
            const ts = timestamp * 1000;
            if (!dataMap.has(ts)) {
              dataMap.set(ts, {
                timestamp: ts,
                formattedTime: "",
              });
            }
            const point = dataMap.get(ts)!;
            point[key] = (point[key] as number || 0) + parseFloat(value);
          }
        }
      }

      // Process TX data
      if (txResponse.data.status === "success" && txResponse.data.data?.result) {
        for (const series of txResponse.data.data.result) {
          const key = buildSeriesKey(series.metric, "TX");
          keysSet.add(key);

          for (const [timestamp, value] of series.values) {
            const ts = timestamp * 1000;
            if (!dataMap.has(ts)) {
              dataMap.set(ts, {
                timestamp: ts,
                formattedTime: "",
              });
            }
            const point = dataMap.get(ts)!;
            point[key] = (point[key] as number || 0) + parseFloat(value);
          }
        }
      }

      const processedData = Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
      const timeFormat = getAdaptiveTimeFormat(processedData);
      processedData.forEach((point) => {
        point.formattedTime = dayjs(point.timestamp).tz(timezoneValue).format(timeFormat);
      });
      const keys = Array.from(keysSet);

      setChartData(processedData);
      setSeriesKeys(keys);
    } catch (err: any) {
      console.error("Failed to fetch metrics:", err);
      setError(getMetricsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseChart
      title="Agent Network"
      loading={loading}
      error={error}
      chartData={chartData}
      seriesKeys={seriesKeys}
      formatYAxis={formatBytesPerSecond}
      formatTooltip={formatBytesPerSecond}
      timezoneLabel={timezoneLabel}
    />
  );
};
